import { P, H3 } from '@sellpy/design-system-react-web'
import styled, { useTheme } from 'styled-components'
import { fromJS } from 'immutable'
import { dynamicDescription, money, readableUrls } from '@sellpy/commons'
import { Link } from 'react-router-dom'
import { ItemTitle, ItemSubtitle, ItemLink } from '../../../components/ItemOutputComponents.jsx'
import HoverImage from '../../../components/HoverImage.jsx'
import { selectImagesByItemStatusImmutable, selectImagesForUse } from '../../../item/tools'
import { locale, localizedMetadata } from '../../../../common/region/locale'
import { PDP_TYPE } from '../../../item/constants'
import { useImageAlt } from '../../../item/components/hooks/useImageAlt'
import { DataLayer } from '../../../../common/analytics/dataLayer.js'
import { getAnalyticsItem } from '../../../../common/analytics/getAnalyticsItem.js'

const Container = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 8px;
`
const StyledItemTitle = styled(ItemTitle)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const TopRightContainer = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
`

const TopLeftContainer = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;
`

const PriceContainer = styled.div`
  display: flex;
`

const InfoBlobsContainer = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  z-index: 1;
`

const TextContainer = styled.div`
  margin: 0.5rem 0;
`

/**
 * @param {Object} props
 * @param {Object} props.item
 * @param {Object} [props.priceOrValue]
 * @param {Function} [props.onClick]
 * @param {any} [props.infoBlobs]
 * @param {any} [props.bottom]
 * @param {string} [props.rejectReason]
 */
const ItemTile = ({
  item,
  priceOrValue,
  onClick,
  topRight,
  topLeft,
  infoBlobs,
  bottom,
  className,
  rejectReason,
  pdpType = PDP_TYPE.BUYER,
  analyticsProps
}) => {
  if (!(item instanceof Map)) item = fromJS(item)
  if (!(priceOrValue instanceof Map)) priceOrValue = fromJS(priceOrValue)

  analyticsProps = analyticsProps || { objectID: item.get('objectID') || item.get('objectId') }

  const images = selectImagesByItemStatusImmutable(item)

  const handleClick = (pdpType) => {
    if (onClick) onClick()
    getAnalyticsItem(item.get('objectID') || item.get('objectId')).then((result) => {
      if (result) DataLayer.trackItemClick({ ...result, pdpType: pdpType })
    })
  }

  return (
    <Container
      className={className}
      itemScope
      itemType='http://schema.org/Product'
      {...analyticsProps}
    >
      <meta itemProp='brand' content={item.get('metadata')?.get('brand')} />
      <TopRightContainer key={`${item.get('objectID')}-topRightInfoBubble`}>
        {topRight}
      </TopRightContainer>
      <TopLeftContainer key={`${item.get('objectID')}-topLeftInfoBubble`}>
        {topLeft}
      </TopLeftContainer>
      {pdpType === PDP_TYPE.BUYER ? (
        <ItemLink
          key={`${item.get('objectID')}-link`}
          item={item}
          onClick={() => handleClick(pdpType)}
          style={{
            textDecoration: 'none',
            display: 'flex',
            flexDirection: 'column'
          }}
          itemProp='url'
        >
          <ItemInfo
            infoBlobs={infoBlobs}
            images={images}
            item={item}
            rejectReason={rejectReason}
            priceOrValue={priceOrValue}
          />
        </ItemLink>
      ) : (
        <Link
          to={item.get('p2p') ? readableUrls.myCircleItemLink(item) : readableUrls.myItemLink(item)}
          className={className}
          onClick={() => handleClick(pdpType)}
          style={{
            textDecoration: 'none',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <ItemInfo
            infoBlobs={infoBlobs}
            images={images}
            item={item}
            rejectReason={rejectReason}
            priceOrValue={priceOrValue}
          />
        </Link>
      )}
      {bottom && <div>{bottom}</div>}
    </Container>
  )
}

const ItemInfo = ({ infoBlobs, images, item, rejectReason, priceOrValue }) => {
  return (
    <>
      <div style={{ position: 'relative' }}>
        <InfoBlobsContainer>{infoBlobs}</InfoBlobsContainer>
        <ItemImage images={images} localizedMetadata={item.get('metadata')?.toJS()} />
      </div>
      <TextContainer>
        <StyledItemTitle
          design='body4'
          component={H3}
          item={item}
          defaultValue='&nbsp;'
          noMargin
          itemProp='name'
        />
        <Subtitle item={item} rejectedItem={Boolean(rejectReason)} />
      </TextContainer>
      {rejectReason ? (
        <P italic center>
          {rejectReason}
        </P>
      ) : (
        <Price priceOrValue={priceOrValue} />
      )}
    </>
  )
}

const ItemImage = ({ images, localizedMetadata }) => {
  const getAlt = useImageAlt()
  const selectedImages = selectImagesForUse(images)
  return (
    <HoverImage
      itemProp='image'
      primaryImage={selectedImages.get(0)}
      secondaryImage={selectedImages.get(1)}
      altPrimary={getAlt({ localizedMetadata, index: 0 })}
      altSecondary={getAlt({ localizedMetadata, index: 1 })}
      darken
    />
  )
}

const Price = ({ priceOrValue }) => (
  <PriceContainer itemProp='offers' itemScope itemType='http://schema.org/Offer'>
    <meta itemProp='priceCurrency' content={priceOrValue?.get('currency')} />
    <P
      itemProp='price'
      content={priceOrValue && money.toBaseUnit(priceOrValue).get('amount')}
      noMargin
      design={'body4'}
    >
      {(priceOrValue && money.formatSubUnit(priceOrValue)) || <>&nbsp;</>}
    </P>
  </PriceContainer>
)

const rejectedItemTitleOrder = ['brand', 'type', 'size', 'model', 'color', 'material']
const Subtitle = ({ rejectedItem, item }) => {
  const theme = useTheme()
  if (rejectedItem) {
    return (
      <P design={'body3'} style={{ color: theme.color.grey.shade3 }} noMargin>
        {dynamicDescription.itemSubtitle({
          metadata: item.get(localizedMetadata) || item.get('metadata'),
          titleOrder: rejectedItemTitleOrder,
          locale
        })}
      </P>
    )
  } else {
    return (
      <StyledItemTitle
        as={ItemSubtitle}
        design={'body3'}
        component={P}
        item={item}
        defaultValue='&nbsp;'
        style={{ color: theme.color.grey.shade3 }}
        noMargin
      />
    )
  }
}

export default ItemTile
